// src/BudgetPage.js
//
// UPDATED to full CRUD. Kept the calculated "Remaining" column and
// click-to-sort header from the read-only version. Uses project_label.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import BudgetForm from './BudgetForm';

function BudgetPage({ token }) {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortAscending, setSortAscending] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);

  const refreshBudgets = () => {
    fetch(`${API_BASE_URL}/api/budgets/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setBudgets(data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshBudgets();
  }, [token]);

  const withRemaining = budgets.map((b) => ({
    ...b,
    remaining: parseFloat(b.allocated_amount) - parseFloat(b.spent_amount),
  }));

  const sortedBudgets = sortAscending === null
    ? withRemaining
    : [...withRemaining].sort((a, b) =>
        sortAscending ? a.remaining - b.remaining : b.remaining - a.remaining
      );

  const toggleSort = () => setSortAscending((prev) => (prev === null ? true : !prev));

  const handleNewClick = () => { setEditingBudget(null); setShowForm(true); };
  const handleEditClick = (b) => { setEditingBudget(b); setShowForm(true); };
  const handleSaved = () => { setShowForm(false); setEditingBudget(null); refreshBudgets(); };
  const handleCancel = () => { setShowForm(false); setEditingBudget(null); };

  const handleDelete = async (b) => {
    const confirmed = window.confirm(`Delete budget "${b.donor}"? This cannot be undone.`);
    if (!confirmed) return;
    await fetch(`${API_BASE_URL}/api/budgets/${b.id}/`, {
      method: 'DELETE',
      headers: { Authorization: `Token ${token}` },
    });
    refreshBudgets();
  };

  if (loading) return <p className="text-gray-500">Loading budgets...</p>;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Budgets</h1>
        <button onClick={handleNewClick} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          + New Budget
        </button>
      </div>

      {showForm && (
        <BudgetForm token={token} budget={editingBudget} onSaved={handleSaved} onCancel={handleCancel} />
      )}

      <table className="w-full bg-white rounded shadow">
        <thead>
          <tr className="text-left border-b">
            <th className="p-3">Project</th>
            <th className="p-3">Donor</th>
            <th className="p-3">Allocated</th>
            <th className="p-3">Spent</th>
            <th className="p-3 cursor-pointer select-none hover:underline" onClick={toggleSort}>
              Remaining {sortAscending === null ? '' : sortAscending ? 'â–²' : 'â–¼'}
            </th>
            <th className="p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedBudgets.map((b) => (
            <tr key={b.id} className="border-b hover:bg-gray-50">
              <td className="p-3">{b.project_label}</td>
              <td className="p-3">{b.donor}</td>
              <td className="p-3">{parseFloat(b.allocated_amount).toFixed(2)}</td>
              <td className="p-3">{parseFloat(b.spent_amount).toFixed(2)}</td>
              <td className="p-3">
                <span className={b.remaining < 0 ? 'text-red-600 font-semibold' : 'text-gray-800'}>
                  {b.remaining.toFixed(2)}
                </span>
              </td>
              <td className="p-3">
                <button onClick={() => handleEditClick(b)} className="text-blue-600 text-sm hover:underline mr-3">Edit</button>
                <button onClick={() => handleDelete(b)} className="text-red-600 text-sm hover:underline">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default BudgetPage;
